import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollReveal } from '@/components/motion'
import type { IndustryData } from '@/data/industries'
import { manualTime, shortTime } from './listing'
import { stepAgent } from './WorkflowDiagram'

const FILL = ['bg-[var(--color-landing-accent)]', 'bg-[#ff00f2]', 'bg-[var(--color-landing-secondary)]', 'bg-[#0a0a0a]'] as const

/**
 * The workflows as large hover rows (home page DirectionalList grammar): a
 * big faded number, the workflow name in display type, trigger → output on
 * the right. On hover a colour band wipes down, the text flips white and the
 * workflow's steps rise into a reserved strip. Transform/opacity only.
 */
export function WorkflowRows({ industry }: { industry: IndustryData }) {
  return (
    <div className="border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
      {industry.workflows.map((workflow, i) => (
        <ScrollReveal key={workflow.name} direction="none" delay={i * 0.06}>
          <Link
            to="/platform/ai-generation"
            className="group relative block overflow-hidden border-b border-[var(--color-landing-gray-200)] focus-visible:outline-offset-[-4px] dark:border-[var(--color-landing-gray-800)]"
          >
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 origin-top scale-y-0 transition-transform duration-300 ease-[cubic-bezier(0.76,0,0.24,1)]',
                '[@media(hover:hover)_and_(pointer:fine)]:group-hover:scale-y-100',
                FILL[i % FILL.length]
              )}
            />
            <span className="landing-container relative z-10 grid items-center gap-x-10 gap-y-3 py-8 md:py-10 lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,34ch)]">
              <span
                aria-hidden
                className={cn(
                  'font-[family-name:var(--font-landing-subdisplay)] text-[clamp(3.5rem,7vw,7.5rem)] font-light leading-none tracking-[-0.04em] text-black/10 transition-colors duration-300 dark:text-white/15',
                  '[@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white/40'
                )}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="min-w-0">
                <span
                  className={cn(
                    'landing-text-display-xl block text-[var(--color-landing-gray-900)] transition-colors duration-300 dark:text-white',
                    '[@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white'
                  )}
                >
                  {workflow.name}
                </span>
                {/* Steps strip: rises in on hover (fine pointers) */}
                <span aria-hidden className="mt-4 hidden flex-wrap gap-2 lg:flex">
                  {workflow.steps.map((step, s) => {
                    const agent = stepAgent(step, industry.aiAgents)
                    return (
                      <span
                        key={`${step}-${s}`}
                        className={cn(
                          'flex translate-y-4 items-center gap-2 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#0a0a0a] opacity-0 shadow-[0_10px_24px_-12px_rgba(0,0,0,0.35)] transition-[opacity,translate] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                          '[@media(hover:hover)_and_(pointer:fine)]:group-hover:translate-y-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100'
                        )}
                        style={{ transitionDelay: `${s * 60}ms` }}
                      >
                        <span className="flex h-4 w-4 items-center justify-center bg-[#0a0a0a] font-[family-name:var(--font-landing-display)] text-[0.5rem] text-white">
                          {agent ? agent.name[0] : s + 1}
                        </span>
                        {step}
                      </span>
                    )
                  })}
                </span>
              </span>
              <span
                className={cn(
                  'landing-text-body-sm flex flex-col gap-2 text-[var(--color-landing-gray-600)] transition-colors duration-300 dark:text-[var(--color-landing-gray-400)] lg:text-right',
                  '[@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white/90'
                )}
              >
                <span className="flex flex-wrap items-center gap-x-2 lg:justify-end">
                  {workflow.trigger}
                  <ArrowRight aria-hidden className="h-4 w-4 shrink-0" />
                  <span className="sr-only">to</span>
                  {workflow.output}
                </span>
                <span
                  className={cn(
                    'text-sm font-bold uppercase tracking-[0.04em] text-[var(--color-landing-gray-900)] transition-colors duration-300 dark:text-white',
                    '[@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white'
                  )}
                >
                  {shortTime(workflow.timeSaved)}
                  {manualTime(workflow.timeSaved) && ` vs. ${manualTime(workflow.timeSaved)}`}
                </span>
              </span>
            </span>
          </Link>
        </ScrollReveal>
      ))}
    </div>
  )
}
